<template>
    <div
        class="overflow-x-hidden px-[20px] lg:px-[40px] border-b border-gray-DFDFDF h-[70px]"
        v-if="!nameHidden"
    >
        <div :class="[
            'flex justify-between items-center overflow-hidden h-full w-full',
            hasError && 'h-[72px]',
        ]">
            <div class="flex flex-col justify-center h-full py-[16px] w-full">
                <!-- <div
                    v-html="placementLabel"
                    class="font-semibold text-[14px] mr-[10px]"
                    :class="hasError && 'text-red-error'"
                /> -->
                <div class="leading-[18px] font-oceanside">
                    <div class="flex">
                        <div class="flex-1">
                            <div class="text-[16px] font-semibold" :class="hasError && 'text-red-error'">
                                <template v-if="showSelections">
                                    {{ $t('SELECTION') }}
                                </template>
                                <template v-else>
                                    {{ $t('CUSTOMIZATIONS') }}
                                </template>
                            </div>
                            <div class="text-[14px]" v-if="!hasError && !hideCombine">
                                {{ $t('SELECT_INDIVIDUALLY_OR_COMBINE') }}
                            </div>
                        </div>
                        <slot name="headerSelected" />
                    </div>
                </div>
                <div class="font-oceanside">
                    <slot />
                </div>
            </div>
            <!-- show if no slot data passed in -->
            <button
                v-if="!placementData"
                type="button"
                class="underline font-oceanside text-[14px]"
                @click="openColorLocationsPanel"
            >
                {{ $t('VIEW_ALL_COLOR_LOCATIONS') }}
            </button>
            <!-- <div
                v-if="hasError"
                class="text-red-error text-[14px] flex flex-col justify-center items-center h-full text-nowrap"
            >
                ─ ─
            </div> -->
            <adaptive-selection-summary
                v-if="isFrontGroup"
                ref="currentSelection"
                class="shrink-0 w-[240px] max-w-[60%] opacity-0 transition-opacity duration-300"
            >
                <template #full>
                    <current-selection
                        context="panel"
                        :placement-data="placementData"
                        :has-errors="hasError"
                        :justify-text-end="true"
                    />
                </template>
                <template #compact>
                    <div class="flex items-center justify-end gap-1 min-w-0 text-[14px] font-oceanside leading-[18px] whitespace-nowrap">
                        <span class="truncate" :title="compactSelectionLabel">
                            {{ compactSelectionLabel }}
                        </span>
                        <span aria-hidden="true">|</span>
                        <button type="button" class="underline shrink-0" @click.stop="openColorLocationsPanel">
                            {{ $t('VIEW_DETAILS') }}
                        </button>
                    </div>
                </template>
            </adaptive-selection-summary>
            <current-selection
                v-else
                ref="currentSelection"
                class="shrink-0 opacity-0 transition-opacity duration-300"
                context="panel"
                :placement-data="placementData"
                :has-errors="hasError"
                :justify-text-end="true"
            />
        </div>

        <info-modal
            v-if="learnMoreVimeo"
            :vimeo="learnMoreVimeo"
        />

        <info-modal
            v-else-if="learnMore"
            :text="learnMore"
        />
    </div>
</template>

<script>
    import InfoModal from './InfoModal.vue';
    import CurrentSelection from './CurrentSelection.vue';
    import AdaptiveSelectionSummary from './AdaptiveSelectionSummary.vue';
    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            AdaptiveSelectionSummary,
            CurrentSelection,
            InfoModal,
        },

        props: {
            placement: Object,
            placementData: Object,
            index: Number,
            hasError: Boolean,
            hideCombine: Boolean,
        },

        watch: {
            placementData: {
                immediate: true,
                handler() {
                },
                deep: true,
            },
        },

        mounted() {
            setTimeout(() => {
                this.$refs.currentSelection.$el.style.opacity = '1';
            }, 400); // to keep "Add" text from flashing
        },

        computed: {
            compactSelectionLabel() {
                const labels = [];
                const initials = this.viewController?.selectedAtPlacement('StampingStyle');
                const stamps = this.viewController?.selectedAtPlacement('Stamp');
                const paintfill = this.viewController?.selectedAtPlacement('PutterFrontPaintfill');
                const noCharacterStampingSelected = initials?.code === 'StampingStyle-StampingStyle-1';
                const noStampSelected = stamps?.code === 'Stamp-Stamp-Blank' || stamps?.code === 'Stamp-Stamp-NoThanks';

                if (initials && !initials.placeholder && !noCharacterStampingSelected) {
                    labels.push(this.$t('CATEGORY_INITIALS'));
                }
                if (stamps && !stamps.placeholder && !noStampSelected) {
                    labels.push(this.$t('CATEGORY_STAMPS'));
                }
                if (paintfill && !paintfill.placeholder) {
                    labels.push(this.$t('PAINTFILL'));
                }

                return labels.join(', ');
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            isFrontGroup() {
                return this.placementData?.name === 'Face/Sole' || this.placementData?.code === 'Face/Sole';
            },

            caption() {
                return this.placement.custom?.['v-caption'];
            },

            learnMoreVimeo() {
                return this.placement.custom?.['v-learn-more-vimeo'];
            },

            learnMore() {
                return this.placement.custom?.['v-learn-more'];
            },

            nameHidden() {
                return this.placement.custom?.['v-name-hidden'] === 'yes';
            },

            placementLabel() {
                const name = this.placement.name.trim();

                const price = this.pricedOncePrice ? ` <span class="ml-1 font-semibold">${this.pricedOncePrice}</span>` : '';

                return `${name}${price}`;
            },

            selectedValue() {
                const { selectedValue } = this.placement;
                const selectedComponent = this.placement.components.find((x) => x.code === selectedValue);

                if (!selectedComponent) {
                    return '';
                }

                if (selectedComponent.description[0].name === 'None') {
                    return '─ ─';
                }

                return selectedComponent.description[0].name;
            },

            pricedOnce() {
                if (this.placement?.components) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const option of this.placement.components) {
                        if (option.priceOnce) {
                            return option;
                        }
                    }
                }

                return null;
            },

            pricedOncePrice() {
                const once = this.pricedOnce;

                if (once) {
                    return this.customizerStore.getOptionPrice(this.viewController, once);
                }

                return null;
            },

            showSelections() {
                const customizationGroupCodes = [
                    'Grip',
                    'Headcover',
                    'PutterShaftBand',
                ];

                const codes = [
                    this.placementData?.code,
                    this.placementData?.name,
                    this.placement?.code,
                    this.placement?.name,
                ];

                return codes.some((code) => customizationGroupCodes.includes(code));
            },
        },

        methods: {
            openColorLocationsPanel() {
                this.customizerStore.openAllColorLocationsPanel({
                    source: 'PlacementHeaderAlt',
                    selectedGroups: this.visibleGroups?.map((group) => group.code) || [],
                });
            },
        },
    };
</script>
